import React, { useEffect } from "react";
import propTypes from "prop-types";
import extractProps from "./extractProps";

import useFormBrain, { useFormContext } from "./formBrain";

/**
 * @typedef {import("./formBrain").FormError} FormError
 * @typedef {import("./formBrain").Validator} Validator
 */

/**
 * Creates a new SmartForm form.
 *
 * Any props not listed below will be passed to the form element directly.
 *
 * @param {Object} props
 * @param {string} props.id The idea of the form.
 * @param {string} [props.action=""] The url to submit the form to.
 * @param {string} [props.method="post"] The HTTP method to use when submitting.
 * @param {Object} [props.defaults={}] Default values for fields in the form.
 * @param {FormError[]} [props.errors=[]] Existing error
 *   messages for fields.
 * @param {Object<string, Validator | Validator[]>} [props.validation={}]
 *   Validation methods for the fields in the form.
 * @param {boolean} [props.revising=false] True if the form should start in
 *   revising mode (fields will be validated on change).
 * @param {Function} [props.onSubmit] Submission handler. This will not be
 *   called if the form does not pass validation.
 * @param {Object} [props.contextRef] React ref to store a pointer to the
 *   current instance of the form brain in.
 * @param {any} [props.children] Child elements.
 *
 * @returns {Object} The form.
 */
const Form = ({
    id,
    action = "",
    method = "post",
    defaults = {},
    errors = [],
    validation = {},
    revising = false,
    onSubmit,
    contextRef,
    children,
    ...props
}) => {
    const brain = useFormBrain(defaults, errors, validation, revising);
    const scope = brain.createScope(id);

    // Extract the values from the fields after the first render. This allows
    // for fields with their own defaults to be initialized properly.
    useEffect(() => {
        const { defaults: values } = extractProps(document.getElementById(id));

        brain.setValues(values);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (contextRef) {
        contextRef.current = brain;
    }

    const handleSubmit = (e) => {
        const errors = brain.validate(e.target);

        if (errors.length > 0) {
            e.preventDefault();
            return;
        }

        if (onSubmit) {
            onSubmit(e);
        }
    };

    return (
        <scope.Provider value={scope.value}>
            <form
                id={id}
                action={action}
                method={method}
                onSubmit={handleSubmit}
                { ...props }
            >
                { children }
            </form>
        </scope.Provider>
    );
};

Form.propTypes = {
    id: propTypes.string.isRequired,
    action: propTypes.string,
    method: propTypes.string,
    defaults: propTypes.object,
    errors: propTypes.arrayOf(propTypes.shape({
        name: propTypes.string,
        message: propTypes.string,
    })),
    validation: propTypes.object,
    revising: propTypes.bool,
    onSubmit: propTypes.func,
    contextRef: propTypes.shape({
        current: propTypes.any,
    }),
    children: propTypes.any,
};

export default Form;
export {
    useFormContext,
};
